import { api } from '@/lib/api';
import { getChecklistFileContent } from './api';

export interface InstallationIqSensorDetail {
  id: string;
  mongoSensorId: string;
  name: string;
  currentValue: string | number | null;
  lastReadingAt: string | null;
  sensorTypeName: string | null;
  sensorTypeUnit: string | null;
  customId: string | null;
  equipmentType: string | null;
  manufacturer: string | null;
  model: string | null;
  serialNumber: string | null;
  streamName: string | null;
  mongoStreamId: string | null;
  probeSerial: string | null;
  probeType: number | null;
  calibrationDate: string | null;
  calibrationDue: string | null;
  calibrationCertNumber: string | null;
  transmitterId: string | null;
  mongoTransmitterId: string | null;
  transmitterName: string | null;
  mongoOrganisationId: string | null;
  installationQualification?: {
    mongoInstallationQualificationId: string;
    reportNumber: string;
    state: string;
  } | null;
  quotes: string[];
  account: {
    id: string;
    name: string;
    accountNumber: string;
  } | null;
  site: {
    id: string;
    name: string;
    mongoSiteId: string | null;
  } | null;
  location: {
    id: string;
    name: string;
    mongoLocationId: string | null;
  } | null;
}

export interface InstallationIqTransmitterDetail {
  id: string;
  mongoTransmitterId: string;
  devId: string;
  deviceType: string | null;
  name: string | null;
  currentFirmwareName: string | null;
  mongoFirmwareDistributionId: string | null;
  quoteNumber: string | null;
  ticketNumber: string | null;
  powerExternal: boolean | null;
  batteryVoltage: string | number | null;
  rssi: number | null;
  iccid: string | null;
  simprovider: string | null;
  hardwareSpecs: Array<{
    k: string;
    i?: string | number | null;
    n?: string | null;
    v?: string | number | null;
  }> | null;
  configs: Array<{
    k: string;
    v?: string | number | boolean | null;
    s?: string | number | boolean | null;
  }> | null;
  siteId: string | null;
  siteName: string | null;
  locationId: string | null;
  locationName: string | null;
  mongoSiteId: string | null;
  mongoLocationId: string | null;
}

export interface InstallationIqContext {
  sensor: InstallationIqSensorDetail;
  transmitter: InstallationIqTransmitterDetail | null;
}

export interface LegacyIqRecord {
  _id: string;
  report_number: string;
  state?: string;
}

export interface LegacyIqFormValues {
  quoteNumber: string;
  ticketNumber: string;
  application: string;
  equipmentDescription: string;
  locationMongoId: string;
  locationName: string;
  siteMongoId: string;
  siteName: string;
  manufacturer: string;
  model: string;
  serialNumber: string;
  transmitterDisplay: string;
  transmitterPowerExternal?: boolean | null;
  transmitterPowerSource: string;
  transmitterExtension: string;
  transmitterSplitter: string;
  transmitterPosition: string;
  transmitterAnchor: string;
  probeType: string;
  probeBottle: string;
  probePosition: string;
  probePositionOther: string;
  probeEntryPoint: string;
  probeExtension: string;
  miscWiresSecured: string;
  miscLogoPlaced: string;
}

export interface IqTechnicianEvidence {
  fileId: string;
  label: string;
}

export interface CreateInstallationIqInput {
  jobId: string;
  organisationId: string;
  organisationName: string;
  responsibleName: string;
  sensor: InstallationIqSensorDetail;
  transmitter: InstallationIqTransmitterDetail;
  formValues: LegacyIqFormValues;
  evidence: IqTechnicianEvidence[];
}

interface V4SensorIqResponse {
  success: boolean;
  sensor: InstallationIqSensorDetail;
}

interface V4TransmitterIqResponse {
  transmitter: InstallationIqTransmitterDetail;
}

interface LegacyInstallationIqTransmitterDetail {
  _id?: string;
  dev_id?: string;
  device_type?: string | null;
  name?: string | null;
  current_firmware_name?: string | null;
  firmwaredistribution?: string | { _id?: string } | null;
  quote_number?: string | null;
  ticket_number?: string | null;
  power_external?: boolean | null;
  battery_voltage?: string | number | null;
  rssi?: number | null;
  ICCID?: string | null;
  simprovider?: string | null;
  hardware_specs?: InstallationIqTransmitterDetail['hardwareSpecs'];
  configs?: InstallationIqTransmitterDetail['configs'];
}

interface V4LegacyTransmitterIqResponse {
  transmitter: LegacyInstallationIqTransmitterDetail;
}

interface LegacyInstallationIqStreamDetail {
  _id?: string;
  probe_serial?: string | null;
  probe_type?: number | null;
  calibration?: {
    date?: string | null;
    due?: string | null;
    cert_number?: string | null;
  } | null;
}

type V4LegacyStreamIqResponse =
  | LegacyInstallationIqStreamDetail[]
  | LegacyInstallationIqStreamDetail;

function getHardwareSpecId(
  specs: InstallationIqTransmitterDetail['hardwareSpecs'],
  hardwareType: string,
): string | null {
  const hardware = specs?.find((item) => item.k === hardwareType);
  if (!hardware?.i) return null;
  const hardwareId = String(hardware.i).trim();
  if (!hardwareId) return null;
  return hardwareId.toLowerCase().startsWith('0x') ? hardwareId : `0x${hardwareId}`;
}

type InstallationIqHardwareType = 'base' | 'module' | 'connectivity';

const INSTALLATION_IQ_HARDWARE_CONFIG_KEYS: Record<InstallationIqHardwareType, string> = {
  base: 'baseID',
  module: 'moduleID',
  connectivity: 'connectID',
};

function getReferenceId(reference: string | { _id?: string } | null | undefined): string | null {
  if (typeof reference === 'string') return reference;
  return reference?._id ?? null;
}

function mergeHardwareSpecs(
  primary: InstallationIqTransmitterDetail['hardwareSpecs'],
  fallback: InstallationIqTransmitterDetail['hardwareSpecs'],
): InstallationIqTransmitterDetail['hardwareSpecs'] {
  const merged = [...(primary ?? [])];
  for (const fallbackSpec of fallback ?? []) {
    if (!merged.some((spec) => spec.k === fallbackSpec.k)) {
      merged.push(fallbackSpec);
    }
  }
  return merged.length > 0 ? merged : null;
}

function normalizeLegacyTransmitter(
  transmitter: LegacyInstallationIqTransmitterDetail,
  mongoTransmitterId: string,
): InstallationIqTransmitterDetail {
  return {
    id: transmitter._id ?? mongoTransmitterId,
    mongoTransmitterId: transmitter._id ?? mongoTransmitterId,
    devId: transmitter.dev_id ?? '',
    deviceType: transmitter.device_type ?? null,
    name: transmitter.name ?? null,
    currentFirmwareName: transmitter.current_firmware_name ?? null,
    mongoFirmwareDistributionId: getReferenceId(transmitter.firmwaredistribution),
    quoteNumber: transmitter.quote_number ?? null,
    ticketNumber: transmitter.ticket_number ?? null,
    powerExternal: transmitter.power_external ?? null,
    batteryVoltage: transmitter.battery_voltage ?? null,
    rssi: transmitter.rssi ?? null,
    iccid: transmitter.ICCID ?? null,
    simprovider: transmitter.simprovider ?? null,
    hardwareSpecs: transmitter.hardware_specs ?? null,
    configs: transmitter.configs ?? null,
    siteId: null,
    siteName: null,
    locationId: null,
    locationName: null,
    mongoSiteId: null,
    mongoLocationId: null,
  };
}

function mergeTransmitterDetails(
  primary: InstallationIqTransmitterDetail,
  fallback: InstallationIqTransmitterDetail,
): InstallationIqTransmitterDetail {
  return {
    ...fallback,
    ...primary,
    currentFirmwareName: primary.currentFirmwareName ?? fallback.currentFirmwareName,
    deviceType: primary.deviceType ?? fallback.deviceType,
    mongoFirmwareDistributionId: primary.mongoFirmwareDistributionId ?? fallback.mongoFirmwareDistributionId,
    quoteNumber: primary.quoteNumber ?? fallback.quoteNumber,
    ticketNumber: primary.ticketNumber ?? fallback.ticketNumber,
    powerExternal: primary.powerExternal ?? fallback.powerExternal,
    batteryVoltage: primary.batteryVoltage ?? fallback.batteryVoltage,
    rssi: primary.rssi ?? fallback.rssi,
    iccid: primary.iccid ?? fallback.iccid,
    simprovider: primary.simprovider ?? fallback.simprovider,
    hardwareSpecs: mergeHardwareSpecs(primary.hardwareSpecs, fallback.hardwareSpecs),
    configs: primary.configs?.length ? primary.configs : fallback.configs,
  };
}

function isMissingIqHardware(transmitter: InstallationIqTransmitterDetail): boolean {
  return (['module', 'base', 'connectivity'] as const).some(
    (hardwareType) => !getInstallationIqHardwareId(transmitter, hardwareType),
  );
}

// 'milesight_ts301' -> 'Milesight TS301'; single-segment types are capitalized as-is.
export function formatTransmitterDeviceModel(deviceType: string | null | undefined): string | null {
  if (!deviceType?.trim()) return null;
  const [vendor, ...model] = deviceType.trim().split('_');
  const vendorLabel = vendor.charAt(0).toUpperCase() + vendor.slice(1).toLowerCase();
  if (!model.length) return vendorLabel;
  return `${vendorLabel} ${model.join(' ').toUpperCase()}`;
}

function isMissingText(value: string | null | undefined): boolean {
  return !value?.trim();
}

function isMissingIqStreamMetadata(sensor: InstallationIqSensorDetail): boolean {
  return isMissingText(sensor.probeSerial)
    || isMissingText(sensor.calibrationDate)
    || isMissingText(sensor.calibrationCertNumber);
}

function preferText(
  primary: string | null | undefined,
  fallback: string | null | undefined,
): string | null {
  if (!isMissingText(primary)) return primary ?? null;
  return isMissingText(fallback) ? null : fallback ?? null;
}

function mergeStreamMetadata(
  sensor: InstallationIqSensorDetail,
  fallback: LegacyInstallationIqStreamDetail,
): InstallationIqSensorDetail {
  return {
    ...sensor,
    probeSerial: preferText(sensor.probeSerial, fallback.probe_serial),
    probeType: sensor.probeType ?? fallback.probe_type ?? null,
    calibrationDate: preferText(sensor.calibrationDate, fallback.calibration?.date),
    calibrationDue: preferText(sensor.calibrationDue, fallback.calibration?.due),
    calibrationCertNumber: preferText(
      sensor.calibrationCertNumber,
      fallback.calibration?.cert_number,
    ),
  };
}

function getConfigValue(
  configs: InstallationIqTransmitterDetail['configs'],
  configType: string,
): string | number | boolean | null {
  const config = configs?.find((item) => item.k === configType);
  return config?.v ?? config?.s ?? null;
}

function convertNumberToHex(value: string | number | boolean | null): string | null {
  if (value === null || value === '') return null;
  if (typeof value === 'string' && value.toLowerCase().startsWith('0x')) return value;
  const num = typeof value === 'boolean'
    ? Number(value)
    : typeof value === 'number'
      ? value
      : parseInt(value, 10);
  if (!Number.isFinite(num)) return null;
  const hex = num.toString(16).toUpperCase();
  return `0x${hex.length % 2 === 0 ? hex : `0${hex}`}`;
}

export function getInstallationIqDisplay(
  transmitter: InstallationIqTransmitterDetail,
): string | null {
  return convertNumberToHex(getConfigValue(transmitter.configs, 'display_enabled'));
}

export function getInstallationIqHardwareId(
  transmitter: InstallationIqTransmitterDetail,
  hardwareType: InstallationIqHardwareType,
): string | null {
  return getHardwareSpecId(transmitter.hardwareSpecs, hardwareType)
    ?? convertNumberToHex(getConfigValue(
      transmitter.configs,
      INSTALLATION_IQ_HARDWARE_CONFIG_KEYS[hardwareType],
    ));
}

function inferProbeTypeName(sensor: InstallationIqSensorDetail, transmitter: InstallationIqTransmitterDetail): string | null {
  const moduleId = getInstallationIqHardwareId(transmitter, 'module');
  if (moduleId) return moduleId;
  if (typeof sensor.probeType === 'number') {
    return `0x${sensor.probeType.toString(16).toUpperCase().padStart(2, '0')}`;
  }
  return null;
}

function formatSensorValue(sensor: InstallationIqSensorDetail): string | null {
  if (sensor.currentValue === null || sensor.currentValue === undefined || sensor.currentValue === '') {
    return null;
  }

  const unit = sensor.sensorTypeUnit ? ` ${sensor.sensorTypeUnit}` : '';
  const timestamp = sensor.lastReadingAt
    ? ` @ ${new Date(sensor.lastReadingAt).toLocaleString('en-CA')}`
    : '';

  return `${sensor.currentValue}${unit}${timestamp}`;
}

function buildLocationRef(values: LegacyIqFormValues) {
  if (!values.locationMongoId) return undefined;
  return {
    _id: values.locationMongoId,
    name: values.locationName || '',
    site: values.siteMongoId
      ? { _id: values.siteMongoId, name: values.siteName || '' }
      : undefined,
  };
}

function buildSiteRef(values: LegacyIqFormValues, sensor: InstallationIqSensorDetail) {
  if (values.siteMongoId) {
    return {
      _id: values.siteMongoId,
      name: values.siteName || '',
    };
  }

  if (sensor.site?.mongoSiteId) {
    return {
      _id: sensor.site.mongoSiteId,
      name: sensor.site.name,
    };
  }

  return undefined;
}

function normalizeUploadedFileId(uploaded: unknown): string {
  if (typeof uploaded === 'string') return uploaded;
  if (uploaded && typeof uploaded === 'object') {
    if ('_id' in uploaded && typeof uploaded._id === 'string') return uploaded._id;
    if (Array.isArray(uploaded) && uploaded[0] && typeof uploaded[0] === 'string') return uploaded[0];
  }
  throw new Error('Unexpected legacy file upload response');
}

async function downloadInstallationJobFile(jobId: string, evidence: IqTechnicianEvidence): Promise<File> {
  const content = await getChecklistFileContent(jobId, evidence.fileId);
  const decoded = atob(content.data);
  const bytes = Uint8Array.from(decoded, (character) => character.charCodeAt(0));
  const extFromPath = evidence.fileId.split('.').pop();
  const extension = extFromPath && extFromPath.length <= 6 ? extFromPath : 'jpg';
  const safeLabel = evidence.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'iq-photo';
  const filename = `${safeLabel}.${extension}`;

  return new File([bytes], filename, { type: content.contentType || undefined });
}

async function uploadLegacyFile(file: File, organisationId: string): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);
  const uploaded = await api.upload<unknown>(`/v4/legacy/v2/files/upload?organisation=${organisationId}`, formData);
  return normalizeUploadedFileId(uploaded);
}

export async function fetchInstallationIqContext(
  mongoSensorId: string,
): Promise<InstallationIqContext> {
  const sensorResponse = await api.get<V4SensorIqResponse>(`/v4/sensors/${mongoSensorId}`);
  let sensor = sensorResponse.sensor;

  if (sensor.mongoStreamId && isMissingIqStreamMetadata(sensor)) {
    try {
      const legacyResponse = await api.get<V4LegacyStreamIqResponse>(
        `/v4/legacy/v2/streams/${sensor.mongoStreamId}`,
        { select: 'probe_serial probe_type calibration' },
      );
      const legacyStream = Array.isArray(legacyResponse)
        ? legacyResponse[0]
        : legacyResponse;
      if (legacyStream) {
        sensor = mergeStreamMetadata(sensor, legacyStream);
      }
    } catch {
      // The legacy read is enrichment only; preserve the PostgreSQL context if
      // legacy metadata is unavailable for this stream.
    }
  }

  let transmitter: InstallationIqTransmitterDetail | null = null;
  if (sensor.mongoTransmitterId) {
    const transmitterResponse = await api.get<V4TransmitterIqResponse>(
      `/v4/transmitters/${sensor.mongoTransmitterId}`,
    );
    transmitter = transmitterResponse.transmitter;

    if (isMissingIqHardware(transmitter)) {
      try {
        const legacyResponse = await api.get<V4LegacyTransmitterIqResponse>(
          `/v4/legacy/transmitters/${sensor.mongoTransmitterId}`,
        );
        const legacyTransmitter = normalizeLegacyTransmitter(
          legacyResponse.transmitter,
          sensor.mongoTransmitterId,
        );
        transmitter = mergeTransmitterDetails(transmitter, legacyTransmitter);
      } catch {
        // The legacy read is enrichment only; keep the PostgreSQL context so the
        // dialog can explain any truly missing hardware fields to the operator.
      }
    }
  }

  return { sensor, transmitter };
}

export async function createInstallationQualificationFromJob(
  input: CreateInstallationIqInput,
): Promise<LegacyIqRecord> {
  const {
    jobId,
    organisationId,
    organisationName,
    responsibleName,
    sensor,
    transmitter,
    formValues,
    evidence,
  } = input;
  const uploadedFileIds = await Promise.all(
    evidence.map(async (item) => {
      const file = await downloadInstallationJobFile(jobId, item);
      return uploadLegacyFile(file, organisationId);
    }),
  );

  const siteRef = buildSiteRef(formValues, sensor);
  const locationRef = buildLocationRef(formValues);
  const created = await api.post<LegacyIqRecord>('/v4/legacy/v2/installationqualification', {
    organisation: organisationId,
    state: 'inprogress',
    responsible_name: responsibleName || undefined,
    sensor: {
      _id: sensor.mongoSensorId,
      name: sensor.name,
      customid: sensor.customId,
      value: formatSensorValue(sensor),
      stream: sensor.mongoStreamId || sensor.streamName
        ? {
            _id: sensor.mongoStreamId ?? undefined,
            name: sensor.streamName ?? '',
          }
        : undefined,
      calibration_cert: sensor.calibrationCertNumber,
      calibration_date: sensor.calibrationDate,
      organisation: {
        _id: organisationId,
        name: organisationName,
      },
      site: siteRef,
      location: locationRef,
      files: uploadedFileIds,
    },
    transmitter: {
      _id: transmitter.mongoTransmitterId,
      name: transmitter.name || transmitter.devId,
      dev_id: transmitter.devId,
      device_type: transmitter.deviceType || undefined,
      site: transmitter.mongoSiteId
        ? {
            _id: transmitter.mongoSiteId,
            name: transmitter.siteName || '',
          }
        : siteRef,
      location: transmitter.mongoLocationId
        ? {
            _id: transmitter.mongoLocationId,
            name: transmitter.locationName || '',
            site: transmitter.mongoSiteId
              ? {
                  _id: transmitter.mongoSiteId,
                  name: transmitter.siteName || '',
                }
              : undefined,
          }
        : locationRef,
      firmware: {
        version: transmitter.currentFirmwareName,
        firmwaredistribution: transmitter.mongoFirmwareDistributionId
          ? {
              _id: transmitter.mongoFirmwareDistributionId,
              name: transmitter.currentFirmwareName || '',
            }
          : undefined,
      },
      base: getInstallationIqHardwareId(transmitter, 'base'),
      module: getInstallationIqHardwareId(transmitter, 'module'),
      connectivity: getInstallationIqHardwareId(transmitter, 'connectivity'),
      display: formValues.transmitterDisplay || getInstallationIqDisplay(transmitter),
      power_external: formValues.transmitterPowerExternal ?? transmitter.powerExternal,
      power_source: formValues.transmitterPowerSource || null,
      extension: formValues.transmitterExtension || null,
      splitter: formValues.transmitterSplitter || null,
      position: formValues.transmitterPosition || null,
      anchor: formValues.transmitterAnchor || null,
      battery_voltage: transmitter.batteryVoltage,
      rssi: transmitter.rssi,
      ICCID: transmitter.iccid,
      simprovider: transmitter.simprovider,
      files: [],
    },
    equipment: {
      type: formValues.application || sensor.equipmentType || 'other',
      description: formValues.equipmentDescription || undefined,
      manufacturer: formValues.manufacturer,
      model: formValues.model,
      serialnumber: formValues.serialNumber,
    },
    probe: {
      serial: sensor.probeSerial,
      type: formValues.probeType || inferProbeTypeName(sensor, transmitter),
      entry_point: formValues.probeEntryPoint || null,
      extension: formValues.probeExtension || null,
      bottle: formValues.probeBottle || null,
      position: formValues.probePosition || null,
      position_other: formValues.probePositionOther || null,
    },
    misc: {
      wires_secured: formValues.miscWiresSecured || null,
      logo_placed: formValues.miscLogoPlaced || null,
    },
    quote_number: formValues.quoteNumber || undefined,
    ticket_number: formValues.ticketNumber || undefined,
  });

  if (!created?._id || !created.report_number) {
    throw new Error('IQ creation response is missing its report identifiers');
  }

  const completedRequest = {
    ...created,
    organisation: organisationId,
    state: 'complete',
    responsible_name: responsibleName || undefined,
    auto_verify: true,
    quote_number: formValues.quoteNumber || undefined,
    ticket_number: formValues.ticketNumber || undefined,
    sensor: {
      _id: sensor.mongoSensorId,
      name: sensor.name,
      customid: sensor.customId,
      value: formatSensorValue(sensor),
      stream: sensor.mongoStreamId || sensor.streamName
        ? {
            _id: sensor.mongoStreamId ?? undefined,
            name: sensor.streamName ?? '',
          }
        : undefined,
      calibration_cert: sensor.calibrationCertNumber,
      calibration_date: sensor.calibrationDate,
      organisation: {
        _id: organisationId,
        name: organisationName,
      },
      site: siteRef,
      location: locationRef,
      files: uploadedFileIds,
    },
    transmitter: {
      _id: transmitter.mongoTransmitterId,
      name: transmitter.name || transmitter.devId,
      dev_id: transmitter.devId,
      device_type: transmitter.deviceType || undefined,
      site: transmitter.mongoSiteId
        ? {
            _id: transmitter.mongoSiteId,
            name: transmitter.siteName || '',
          }
        : siteRef,
      location: transmitter.mongoLocationId
        ? {
            _id: transmitter.mongoLocationId,
            name: transmitter.locationName || '',
            site: transmitter.mongoSiteId
              ? {
                  _id: transmitter.mongoSiteId,
                  name: transmitter.siteName || '',
                }
              : undefined,
          }
        : locationRef,
      firmware: {
        version: transmitter.currentFirmwareName,
        firmwaredistribution: transmitter.mongoFirmwareDistributionId
          ? {
              _id: transmitter.mongoFirmwareDistributionId,
              name: transmitter.currentFirmwareName || '',
            }
          : undefined,
      },
      base: getInstallationIqHardwareId(transmitter, 'base'),
      module: getInstallationIqHardwareId(transmitter, 'module'),
      connectivity: getInstallationIqHardwareId(transmitter, 'connectivity'),
      display: formValues.transmitterDisplay || getInstallationIqDisplay(transmitter),
      power_external: formValues.transmitterPowerExternal ?? transmitter.powerExternal,
      power_source: formValues.transmitterPowerSource || null,
      extension: formValues.transmitterExtension || null,
      splitter: formValues.transmitterSplitter || null,
      position: formValues.transmitterPosition || null,
      anchor: formValues.transmitterAnchor || null,
      battery_voltage: transmitter.batteryVoltage,
      rssi: transmitter.rssi,
      ICCID: transmitter.iccid,
      simprovider: transmitter.simprovider,
      files: [],
    },
    equipment: {
      type: formValues.application || sensor.equipmentType || 'other',
      description: formValues.equipmentDescription || undefined,
      manufacturer: formValues.manufacturer,
      model: formValues.model,
      serialnumber: formValues.serialNumber,
    },
    probe: {
      serial: sensor.probeSerial,
      type: formValues.probeType || inferProbeTypeName(sensor, transmitter),
      entry_point: formValues.probeEntryPoint || null,
      extension: formValues.probeExtension || null,
      bottle: formValues.probeBottle || null,
      position: formValues.probePosition || null,
      position_other: formValues.probePositionOther || null,
    },
    misc: {
      wires_secured: formValues.miscWiresSecured || null,
      logo_placed: formValues.miscLogoPlaced || null,
    },
  };

  await api.put<unknown>(`/v4/legacy/v2/installationqualification/${created._id}`, completedRequest);

  const renderedReport = await api.get<string>(
    `/v4/legacy/v2/installationqualification/${created._id}/html`,
    { pdf: 'false' },
  );
  if (typeof renderedReport !== 'string' || !renderedReport.trim()) {
    throw new Error('V1 could not render the completed IQ report');
  }

  return { ...created, state: 'complete' };
}
